import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import BulkUploadPopup from "../../components/BulkUploadPopup";
import { useLanguage } from "../../context/languageContext";
import { splitList } from "../../helper/listSeparator";


const BOOK_VALIDATION_RULES = [
    { field: 'book_title', type: 'string', message: 'Title is required' },
    { field: 'author_name', type: 'array', message: 'Author names are required' },
    { field: 'related_genres', type: 'array', message: 'Genre names are required' },
    { field: 'published_date', type: 'string', message: 'Published On is required' },
    { field: 'published_by', type: 'string', message: 'Published By is required' },
    { field: 'number_of_pages', type: 'string', message: 'Number of Pages is required' },
    { field: 'tagline', type: 'string', message: 'Tagline is required' },
    { field: 'about_book', type: 'string', message: 'About Book is required' },
    { field: 'link', type: 'string', message: 'Link is required' },
];

const BOOK_COLUMNS = [
    { key: 'book_title', label: 'Book Name' },
    { key: 'author_name', label: 'Author Name' },
    { key: 'related_genres', label: 'Genre Name' },
    { key: 'published_date', label: 'Published On (year)' },
    { key: 'published_by', label: 'Published By' },
    { key: 'number_of_pages', label: 'Pages' },
    { key: 'tagline', label: 'Tagline' },
    { key: 'about_book', label: 'About Book' },
    { key: 'link', label: 'Link' },
];

const BulkUploadBook = ({ isPopupVisible, handleClosePopup, onSuccess }) => {
    const { adminInfo } = useLanguage();

    const [isSubmit, setIsSubmit] = useState(false);

    // Several values in one cell are separated by ";" — a comma belongs to the
    // value ("Martin Luther King, Jr.", "Everyone Communicates, Few Connect").
    const parseListCell = splitList;

    const createAuthorObjects = (authors) => authors.map((author) => ({ authorName: author }));
    const createGenreObjects = (genres) => genres.map((genre) => ({ genreName: genre }));

    const transformedRowValues = (book) => ({
        book_title: book.book_title?.trim() || "",
        author_name: createAuthorObjects(parseListCell(book.author_name)),
        related_genres: createGenreObjects(parseListCell(book.related_genres)),
        published_date: book.published_date?.trim() || "",
        published_by: book.published_by?.trim() || "",
        number_of_pages: book.number_of_pages?.trim() || "",
        tagline: book.tagline?.trim() || "",
        about_book: book.about_book?.trim() || "",
        link: book.link?.trim() || "",
    });

    const getTableRowValues = (value) => (
        <>
            <td className="min_width">{value.book_title}</td>
            <td className="max_width">
                {value.author_name?.map((item, index) => (
                    <li className="list_styled" key={index}>{item.authorName}</li>
                ))}
            </td>
            <td className="max_width">
                {value.related_genres?.map((item, index) => (
                    <li className="list_styled" key={index}>{item.genreName}</li>
                ))}
            </td>
            <td className="min_width">{value.published_date}</td>
            <td className="min_width">{value.published_by}</td>
            <td className="min_width">{value.number_of_pages}</td>
            <td className="max_width">{value.tagline}</td>
            <td className="max_width">{value.about_book}</td>
            <td className="min_width">{value.link}</td>
        </>
    );

    const handleAddBookData = async (values) => {
        setIsSubmit(true);
        try {
            if (values.length >= 50) {
                toast.info("You can only add up to 50 books at a time");
                setIsSubmit(false);
                return;
            };

            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/books/bulk-add`, { values }, { headers });

            if (response.status === true) {
                toast.success(response.message);
                handleClosePopup();
                onSuccess();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("handleAddBookData catch error::", error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error;
            toast.error(errorMessage);
        } finally {
            setTimeout(() => setIsSubmit(false), 300);
        }
    };


    return (
        <BulkUploadPopup
            isPopupVisible={isPopupVisible}
            handleClosePopup={handleClosePopup}
            title="Add Books in Bulk through CSV"
            columns={BOOK_COLUMNS}
            validationRules={BOOK_VALIDATION_RULES}
            transformedRowValues={transformedRowValues}
            getTableRowValues={getTableRowValues}
            onSubmit={handleAddBookData}
            label="Book"
            isSubmit={isSubmit}
        />
    );
};

export default BulkUploadBook;
